import React from 'react'

export interface FigmaSwitchProps {
  /** Whether the switch is currently on/off */
  checked: boolean
  /** Callback when switch state changes */
  onChange: (checked: boolean) => void
  /** Optional label text to display next to the switch */
  label?: React.ReactNode
  /** Additional CSS class names */
  className?: string
  /** Disabled state */
  disabled?: boolean
}

/**
 * FigmaSwitch - A switch component matching Figma design specifications
 *
 * Design specs from Figma:
 * - Switch size: 18px × 10px
 * - Handle size: 7px × 7px
 * - Border radius: 16px (fully rounded)
 * - Track padding: 2px
 * - Colors: #d9d9d9 (off), rgba(0,0,0,0.88) (on)
 * - Shadow: 0px 2px 4px 0px rgba(0,35,11,0.2)
 *
 * @example
 * ```tsx
 * const [enabled, setEnabled] = useState(false)
 *
 * <FigmaSwitch
 *   checked={enabled}
 *   onChange={setEnabled}
 *   label="Dark Mode"
 * />
 * ```
 */
export const FigmaSwitch: React.FC<FigmaSwitchProps> = ({
  checked,
  onChange,
  label,
  className = '',
  disabled = false
}) => {
  const handleClick = () => {
    if (!disabled) {
      onChange(!checked)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!disabled && (e.key === ' ' || e.key === 'Enter')) {
      e.preventDefault()
      onChange(!checked)
    }
  }

  return (
    <div
      className={`flex items-center gap-[16px] select-none ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${className}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role='switch'
      aria-checked={checked}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
    >
      {/* Switch Toggle */}
      <div
        className={`w-[18px] h-[10px] rounded-[16px] transition-colors relative flex items-center ${
          checked ? 'bg-[rgba(0,0,0,0.88)]' : 'bg-[#d9d9d9]'
        }`}
      >
        {/* Handle */}
        <div
          className={`absolute w-[7px] h-[7px] bg-white rounded-full shadow-[0px_2px_4px_0px_rgba(0,35,11,0.2)] transition-transform ${
            checked ? 'translate-x-[9px]' : 'translate-x-[2px]'
          }`}
        />
      </div>

      {/* Label */}
      {label && (
        <p className="font-['Geist',_sans-serif] font-medium leading-[22px] text-[14px] text-[rgba(0,0,0,0.88)] select-none">
          {label}
        </p>
      )}
    </div>
  )
}
